import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { useAppDispatch } from "../../../app/hooks";
import { VocabularySlot } from "../enums/VocabularySlot";
import { VocabularyTerm } from "../models/Organization";
import {
  resetVocabularyTerm,
  setVocabularyTerm,
} from "../slice-extensions/organization-slice-extension";

interface VocabularyTermRowProps {
  organizationId: string;
  locale: string;
  slot: VocabularySlot;
  term: VocabularyTerm;
}

const MAX_LENGTH = 40;
const FORBIDDEN = /[{}<>]/;

/** Trimmed, 1–40 characters, no { } < > — the same rule the API enforces. */
export const isValidVocabularyWord = (word: string): boolean => {
  const trimmed = word.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_LENGTH && !FORBIDDEN.test(trimmed);
};

/**
 * One slot of the vocabulary table: singular and plural inputs saved together
 * when focus leaves the row (moving from one input to the other does not
 * save), and a "Reset to preset" link while the owner's word overrides the
 * preset.
 */
const VocabularyTermRow: React.FC<VocabularyTermRowProps> = ({ organizationId, locale, slot, term }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [one, setOne] = useState(term.one);
  const [other, setOther] = useState(term.other);
  const [invalid, setInvalid] = useState(false);
  const [saved, setSaved] = useState(false);

  // Follow the saved words, but never overwrite what the owner is still
  // typing in the other input while an earlier save comes back.
  const savedTerm = useRef({ one: term.one, other: term.other });
  useEffect(() => {
    const previous = savedTerm.current;
    setOne((current) => (current === previous.one ? term.one : current));
    setOther((current) => (current === previous.other ? term.other : current));
    savedTerm.current = { one: term.one, other: term.other };
  }, [term.one, term.other]);

  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  const flashSaved = () => {
    setSaved(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2000);
  };

  const oneInput = useRef<HTMLInputElement>(null);
  const otherInput = useRef<HTMLInputElement>(null);

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget;
    if (next && (next === oneInput.current || next === otherInput.current)) return;
    if (one.trim() === term.one && other.trim() === term.other) return;
    if (!isValidVocabularyWord(one) || !isValidVocabularyWord(other)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    const result = await dispatch(
      setVocabularyTerm({ organizationId, locale, slot, one: one.trim(), other: other.trim() })
    );
    if (setVocabularyTerm.fulfilled.match(result)) flashSaved();
  };

  const handleReset = async () => {
    const result = await dispatch(resetVocabularyTerm({ organizationId, locale, slot }));
    if (resetVocabularyTerm.fulfilled.match(result)) flashSaved();
  };

  const label = t(`organization.vocabulary.slot.${slot}`);

  return (
    <div className="grid grid-cols-1 gap-2 border-t border-grey-90 py-3 dark:border-primary-30 lg:grid-cols-[10rem_1fr_1fr_auto] lg:items-center">
      <span className="text-sm font-semibold text-grey-20 dark:text-white">{label}</span>
      <input
        type="text"
        className="input w-full"
        ref={oneInput}
        aria-label={`${label} — ${t("organization.vocabulary.singular")}`}
        value={one}
        maxLength={MAX_LENGTH}
        onChange={(e) => setOne(e.target.value)}
        onBlur={handleBlur}
      />
      <input
        type="text"
        className="input w-full"
        ref={otherInput}
        aria-label={`${label} — ${t("organization.vocabulary.plural")}`}
        value={other}
        maxLength={MAX_LENGTH}
        onChange={(e) => setOther(e.target.value)}
        onBlur={handleBlur}
      />
      <div className="flex min-h-6 items-center gap-3 text-sm">
        {term.isOverridden && (
          <button
            type="button"
            className="text-primary-40 hover:underline dark:text-primary-90"
            onClick={handleReset}
          >
            {t("organization.vocabulary.reset")}
          </button>
        )}
        {saved && (
          <span className="flex items-center gap-1 text-green-700 dark:text-green-400">
            <Check size={16} /> {t("organization.vocabulary.saved")}
          </span>
        )}
      </div>
      {invalid && (
        <p className="text-sm text-red-600 dark:text-red-400 lg:col-span-4">
          {t("organization.vocabulary.invalid")}
        </p>
      )}
    </div>
  );
};

export default VocabularyTermRow;
